'use client';

import React, { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import useSWR from 'swr';
import s from './ConnectionsPage.module.scss';
import Section from '../../components/Section/Section';
import { t } from '../../i18n';
import Grid from '../../components/Grid/Grid';
import Box from '../../components/Box/Box';
import FeaturesCard from '../../components/FeaturesCard/FeaturesCard';
import OrganizationChart from '../../components/OrganizationChart/OrganizationChart';
import { getOrganizationChart } from '../../api/connections';
import ProfileCardLink from '../../components/ProfileCardLink/ProfileCardLink';
import IconText from '../../components/IconText/IconText';
import classNames from 'classnames';

export const NetworkTab = ({ features = {}, urlData, selectedCompany }) => {
    const searchParams = useSearchParams();
    const holding = searchParams.get('holding');
    const [orgChartHasLoaded, setOrgChartHasLoaded] = useState(false);

    const { data, isLoading, error } = useSWR(
        holding ? ['org-chart', holding] : null,
        ([, holdingId]) =>
            getOrganizationChart(holdingId, urlData).then(
                (res) => res.json.data
            ),
        {
            revalidateOnFocus: false,
        }
    );

    const organizationChart = data || [];

    // the weirdness here is to make them unique, so we don't show same
    // individual multiple times, if they were to be associated with
    // multiple companies
    const relatedIndividuals = Array.from(
        new Map(
            organizationChart
                .flatMap((item) => item.relatedIndividuals)
                .map((item) => [
                    item.properties.primaryKey_,
                    {
                        ...item.properties,
                        image: { src: item.src },
                        primaryKey_: item.properties.primaryKey_,
                    },
                ])
        ).values()
    );
    return (
        <Section isMainSection modifier="NetworkTab">
            {!selectedCompany ? (
                <div className={s.LayoutWrapper}>
                    <div className={s.EmptyState}>
                        <Grid columns={2}>
                            <Box modifiers={['White', 'ConnectionsPage']}>
                                <IconText
                                    title={t('general.noSelectedCompanyLabel')}
                                    text={t('general.noSelectedCompanyText')}
                                    icon={'captive_portal'}
                                />
                            </Box>
                        </Grid>
                        <Grid columns={2}>
                            <div className={s.EmptyStateHeaderWrapper}>
                                <div className={s.Header}>
                                    <h2 className={s.Title}>
                                        {features.title ||
                                            t('general.features')}
                                    </h2>
                                </div>
                                <div className={s.Container}>
                                    <FeaturesCard features={features} />
                                </div>
                            </div>
                        </Grid>
                    </div>
                </div>
            ) : (
                <div className={s.NetworkTabWrapper}>
                    {organizationChart.length <= 1 && !isLoading ? (
                        <div className={s.ZeroStateWrapper}>
                            <IconText
                                title={t(
                                    'connectionsPage.holdings.noOrganizationDataLabel'
                                )}
                                text={t(
                                    'connectionsPage.holdings.noOrganizationDataText'
                                )}
                                icon={'account_tree'}
                            />
                        </div>
                    ) : (
                        <>
                            <Box modifiers={['Grey', 'AutoHeight']}>
                                <OrganizationChart
                                    urlData={urlData}
                                    organizationChart={organizationChart}
                                    isLoading={isLoading}
                                    setOrgChartHasLoaded={setOrgChartHasLoaded}
                                    selectedCompany={selectedCompany}
                                />
                            </Box>
                            {orgChartHasLoaded && (
                                <Box
                                    modifiers={[
                                        'ConnectionsPage',
                                        'Grey',
                                        'NoPaddingRight',
                                        'NetworkTab',
                                    ]}>
                                    <h2
                                        id="related-individuals"
                                        className={s.RelatedIndividualsTitle}>
                                        Related Individuals
                                    </h2>
                                    <div
                                        className={classNames(
                                            s.FlexWrapContainer,
                                            {
                                                [s.Overflow]:
                                                    relatedIndividuals.length >
                                                    1,
                                            }
                                        )}>
                                        {relatedIndividuals.length > 0 ? (
                                            relatedIndividuals.map(
                                                (individual) => (
                                                    <ProfileCardLink
                                                        key={
                                                            individual.primaryKey_
                                                        }
                                                        {...individual}
                                                    />
                                                )
                                            )
                                        ) : (
                                            <div className={s.ZeroStateWrapper}>
                                                <IconText
                                                    title={t(
                                                        'connectionsPage.holdings.noIndividualsLabel'
                                                    )}
                                                    text={
                                                        selectedCompany
                                                            ? t(
                                                                  'connectionsPage.holdings.noIndividualsText'
                                                              )
                                                            : t(
                                                                  'connectionsPage.holdings.noIndividualsTextAll'
                                                              )
                                                    }
                                                    icon={'account_circle'}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </Box>
                            )}
                        </>
                    )}
                </div>
            )}
        </Section>
    );
};
